import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { trackIntentSet } from '../utils/googleAnalytics';

/**
 * User Intent Types
 * 
 * Simplified intent model based on what users actually need:
 * 
 * PERSONAL:
 * - identity: Email Signature + Digital Card (bundled, synced)
 * - identity_wallet: Full brand kit (signature + card + wallet)
 * - signature_only: Just email signature (enterprise escape hatch)
 * - wallet_only: Just wallet passes (streamlined flow)
 * 
 * COMPANY:
 * - team_identity: Employee signatures/cards with templates
 * - team_full: Team identity + wallet passes
 * - engagement: Customer-facing passes only (coupons, events, loyalty)
 */
export type UserIntent = 
  | 'identity'           // Signature + Card (bundled)
  | 'identity_wallet'    // Signature + Card + Wallet (full kit)
  | 'signature_only'     // Just signature (enterprise)
  | 'wallet_only'        // Just wallet passes
  | 'team_identity'      // Team signatures/cards
  | 'team_full'          // Team + wallet
  | 'engagement'         // Customer passes only
  // Legacy support
  | 'personal_full'
  | 'personal_pass';

/**
 * Intent metadata for UI display
 */
export const INTENT_METADATA: Record<UserIntent, {
  title: string;
  shortTitle: string;
  description: string;
  icon: string;
  features: string[];
}> = {
  identity: {
    title: 'Professional Identity',
    shortTitle: 'Identity',
    description: 'Email signature + Digital card (synced)',
    icon: '✨',
    features: [
      'Branded email signature',
      'Shareable digital card',
      'One profile, always synced',
      'QR code sharing'
    ]
  },
  identity_wallet: {
    title: 'Full Brand Kit',
    shortTitle: 'Full Kit',
    description: 'Signature + Card + Wallet pass',
    icon: '🎯',
    features: [
      'Everything in Identity',
      'Apple & Google Wallet',
      'Works offline',
      'Maximum reach'
    ]
  },
  signature_only: {
    title: 'Email Signature',
    shortTitle: 'Signature',
    description: 'Professional email signature only',
    icon: '✉️',
    features: [
      'Branded closers',
      'Template library',
      'Easy deployment',
      'Enterprise-ready'
    ]
  },
  wallet_only: {
    title: 'Wallet Pass Only',
    shortTitle: 'Wallet Pass',
    description: 'Digital card in your phone\'s wallet',
    icon: '📱',
    features: [
      'Quick 2-min setup',
      'Always accessible',
      'Share via QR/NFC',
      'Works offline'
    ]
  },
  team_identity: {
    title: 'Team Identity',
    shortTitle: 'Team',
    description: 'Signatures and cards for your team',
    icon: '👥',
    features: [
      'Template system',
      'Bulk deployment',
      'Brand consistency',
      'Employee self-service'
    ]
  },
  team_full: {
    title: 'Team Full Kit',
    shortTitle: 'Team Full',
    description: 'Team identity + wallet passes',
    icon: '🏢',
    features: [
      'Everything in Team',
      'Employee wallet passes',
      'Event badges',
      'Complete toolkit'
    ]
  },
  engagement: {
    title: 'Customer Engagement',
    shortTitle: 'Engagement',
    description: 'Wallet passes for customers',
    icon: '🎟️',
    features: [
      'Coupons & offers',
      'Event tickets',
      'Loyalty cards',
      'Distribution tools'
    ]
  },
  // Legacy support (map to new intents)
  personal_full: {
    title: 'Full Brand Kit',
    shortTitle: 'Full Kit',
    description: 'Email signature + Digital card + Wallet pass',
    icon: '✨',
    features: ['Cohesive branding', 'Share anywhere', 'Update once, sync all']
  },
  personal_pass: {
    title: 'Wallet Pass Only',
    shortTitle: 'Wallet Pass',
    description: 'Digital business card in wallet',
    icon: '📱',
    features: ['Quick setup', 'Always accessible', 'Works offline']
  }
};

/**
 * Intent Store State
 */
interface IntentState {
  intent: UserIntent | null;
  userType: 'personal' | 'company' | null;
  hasCompletedOnboarding: boolean;
  selectedAt: string | null;
  intentHistory: Array<{
    intent: UserIntent;
    selectedAt: string;
  }>;
  // Hydration tracking - not persisted
  _hasHydrated: boolean;
}

/**
 * Intent Store Actions
 */
interface IntentActions {
  setIntent: (intent: UserIntent) => void;
  setUserType: (userType: 'personal' | 'company') => void;
  clearIntent: () => void;
  completeOnboarding: () => void;
  needsOnboarding: () => boolean;
  getIntentMetadata: () => typeof INTENT_METADATA[UserIntent] | null;
  // Force set from URL - takes priority over hydrated data
  setIntentFromUrl: (intent: UserIntent) => void;
  setHydrated: () => void;
}

type IntentStore = IntentState & IntentActions;

const initialState: IntentState = {
  intent: null,
  userType: null,
  hasCompletedOnboarding: false,
  selectedAt: null,
  intentHistory: [],
  _hasHydrated: false
};

/**
 * Intent Store
 * 
 * NOTE: Zustand persist hydrates AFTER initial render, which can cause race conditions
 * when setting intent from URL params. Use setIntentFromUrl for URL-based intent
 * to ensure it takes priority over hydrated localStorage data.
 */
export const useIntentStore = create<IntentStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        setIntent: (intent: UserIntent) => {
          const now = new Date().toISOString();
          const currentIntent = get().intent;

          set((state) => ({
            intent,
            selectedAt: now,
            intentHistory: currentIntent && currentIntent !== intent
              ? [...state.intentHistory, { intent, selectedAt: now }]
              : state.intentHistory
          }));

          // Track intent set in GA4
          const source = currentIntent ? 'change' : 'onboarding';
          trackIntentSet(intent, source, currentIntent);

          console.log('[IntentStore] Intent set:', intent);
        },
        
        // Force set from URL - always takes priority
        // This is called from OnboardingGuard when URL has intent param
        setIntentFromUrl: (intent: UserIntent) => {
          const now = new Date().toISOString();
          const currentIntent = get().intent;
          set({
            intent,
            selectedAt: now,
            hasCompletedOnboarding: true
          });

          // Track intent set from URL in GA4
          trackIntentSet(intent, 'url_param', currentIntent);

          console.log('[IntentStore] Intent set from URL (priority):', intent);
        },
        
        setUserType: (userType: 'personal' | 'company') => {
          set({ userType });
          console.log('[IntentStore] User type set:', userType);
        },
        
        clearIntent: () => {
          set({
            intent: null,
            userType: null,
            hasCompletedOnboarding: false,
            selectedAt: null
          });
          console.log('[IntentStore] Intent cleared');
        },
        
        completeOnboarding: () => {
          set({ hasCompletedOnboarding: true });
          console.log('[IntentStore] Onboarding completed');
        },
        
        needsOnboarding: () => {
          const state = get();
          return !state.hasCompletedOnboarding || state.intent === null;
        },
        
        getIntentMetadata: () => {
          const state = get();
          if (!state.intent) return null;
          return INTENT_METADATA[state.intent];
        },
        
        setHydrated: () => {
          set({ _hasHydrated: true });
        }
      }),
      {
        name: 'signatureforward-intent',
        version: 2, // Bump version for new schema
        storage: createJSONStorage(() => localStorage),
        // Don't persist _hasHydrated - it's runtime state
        partialize: (state) => ({
          intent: state.intent,
          userType: state.userType,
          hasCompletedOnboarding: state.hasCompletedOnboarding,
          selectedAt: state.selectedAt,
          intentHistory: state.intentHistory
        }),
        // When hydration completes, mark as hydrated
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.setHydrated();
            console.log('[IntentStore] Hydrated from localStorage:', {
              intent: state.intent,
              userType: state.userType
            });
          }
        }
      }
    ),
    { name: 'IntentStore' }
  )
);

/**
 * Hook for common intent checks
 */
export const useIntent = () => {
  const store = useIntentStore();
  
  // Normalize legacy intents
  const normalizedIntent = (() => {
    if (store.intent === 'personal_full') return 'identity_wallet';
    if (store.intent === 'personal_pass') return 'wallet_only';
    return store.intent;
  })();
  
  // Feature visibility helpers
  // When intent is null (non-logged-in / no onboarding), show full platform (signature + card + wallet)
  const noIntent = normalizedIntent === null;

  const showsSignature = noIntent || [
    'identity', 'identity_wallet', 'signature_only',
    'team_identity', 'team_full', 'personal_full'
  ].includes(normalizedIntent || '');

  const showsCard = noIntent || [
    'identity', 'identity_wallet',
    'team_identity', 'team_full', 'personal_full'
  ].includes(normalizedIntent || '');

  const showsWallet = noIntent || [
    'identity_wallet', 'wallet_only',
    'team_full', 'engagement',
    'personal_full', 'personal_pass'
  ].includes(normalizedIntent || '');
  
  const showsEngagementPasses = normalizedIntent === 'engagement';
  
  const isTeam = ['team_identity', 'team_full'].includes(normalizedIntent || '');
  
  return {
    intent: store.intent,
    normalizedIntent,
    userType: store.userType,
    hasCompletedOnboarding: store.hasCompletedOnboarding,
    needsOnboarding: store.needsOnboarding(),
    metadata: store.getIntentMetadata(),
    hasHydrated: store._hasHydrated,
    
    // Intent type checks
    isIdentity: normalizedIntent === 'identity',
    isIdentityWallet: normalizedIntent === 'identity_wallet',
    isSignatureOnly: normalizedIntent === 'signature_only',
    isWalletOnly: normalizedIntent === 'wallet_only',
    isTeamIdentity: normalizedIntent === 'team_identity',
    isTeamFull: normalizedIntent === 'team_full',
    isEngagement: normalizedIntent === 'engagement',
    
    // Legacy compatibility
    isPersonalFull: normalizedIntent === 'identity_wallet',
    isPersonalPass: normalizedIntent === 'wallet_only',
    
    // Feature visibility
    showsSignature,
    showsCard,
    showsWallet,
    showsGenericPass: showsWallet && !showsEngagementPasses,
    showsEngagementPasses,
    isTeam,
    
    // Actions
    setIntent: store.setIntent,
    setIntentFromUrl: store.setIntentFromUrl,
    setUserType: store.setUserType,
    clearIntent: store.clearIntent,
    completeOnboarding: store.completeOnboarding
  };
};

export const useCurrentIntent = () => useIntentStore((state) => state.intent);

export const useNeedsOnboarding = () => {
  const intent = useIntentStore((state) => state.intent);
  const hasCompletedOnboarding = useIntentStore((state) => state.hasCompletedOnboarding);
  return !hasCompletedOnboarding || intent === null;
};
